import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Lock, RotateCcw } from "lucide-react";

function AgentCard({ a, onChange, onReset }) {
  return (
    <div className="border border-border bg-card rounded-sm p-5 space-y-3" data-testid={`agent-cfg-${a.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-sm bg-accent text-accent-foreground text-xs font-bold flex items-center justify-center">{a.no}</span>
          <span className="font-heading font-bold">{a.name || "—"}</span>
          <span className="text-xs text-muted-foreground">{a.role}</span>
        </div>
        <Button size="sm" variant="ghost" className="rounded-sm text-xs" onClick={onReset} data-testid={`agent-cfg-reset-${a.key}`}>
          <RotateCcw className="w-3 h-3 mr-1" /> Kembalikan default
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Nama</Label>
          <Input data-testid={`agent-cfg-name-${a.key}`} value={a.name} onChange={(e) => onChange("name", e.target.value)} className="mt-1 rounded-sm bg-secondary" /></div>
        <div><Label className="text-xs">Peran</Label>
          <Input data-testid={`agent-cfg-role-${a.key}`} value={a.role} onChange={(e) => onChange("role", e.target.value)} className="mt-1 rounded-sm bg-secondary" /></div>
        <div><Label className="text-xs">Deskripsi singkat</Label>
          <Input data-testid={`agent-cfg-desc-${a.key}`} value={a.desc} onChange={(e) => onChange("desc", e.target.value)} className="mt-1 rounded-sm bg-secondary" /></div>
      </div>
      <div><Label className="text-xs">Tugas & SOP (instruksi yang dipakai AI)</Label>
        <Textarea data-testid={`agent-cfg-task-${a.key}`} value={a.task} onChange={(e) => onChange("task", e.target.value)} className="mt-1 rounded-sm bg-secondary min-h-[130px] text-sm leading-relaxed" /></div>
    </div>
  );
}

export default function AgentSettings() {
  const [agents, setAgents] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [locked, setLocked] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = (data) => { setAgents(data.agents); setDefaults(data.defaults); setLocked(data.locked_rule); };
  useEffect(() => { api.get("/agents/config").then((r) => apply(r.data)).catch(() => toast.error("Gagal memuat pengaturan agent")); }, []);

  const change = (key, f, v) => setAgents((arr) => arr.map((a) => (a.key === key ? { ...a, [f]: v } : a)));
  const reset = (key) => setAgents((arr) => arr.map((a) => (a.key === key ? { ...a, ...defaults[key] } : a)));

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(agents.map((a) => [a.key, { name: a.name, role: a.role, desc: a.desc, task: a.task }]));
      const { data } = await api.put("/agents/config", { agents: payload });
      apply(data);
      toast.success("Pengaturan agent tersimpan — berlaku untuk pesan berikutnya");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="agent-settings-page">
      <PageHeader overline="Tim AI Agent" title="Pengaturan Agent">
        <Button className="rounded-sm" onClick={save} disabled={busy} data-testid="agent-cfg-save-btn">{busy ? "Menyimpan…" : "Simpan Semua"}</Button>
      </PageHeader>
      <div className="p-8 max-w-5xl space-y-5">
        <div className="flex gap-2 p-4 border border-amber-300 bg-amber-50 rounded-sm text-sm text-amber-900" data-testid="agent-cfg-locked-rule">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <div><b>Aturan tetap (tidak bisa diubah):</b> {locked}</div>
        </div>
        {agents.map((a) => <AgentCard key={a.key} a={a} onChange={(f, v) => change(a.key, f, v)} onReset={() => reset(a.key)} />)}
      </div>
    </div>
  );
}
